import { useState, useMemo } from 'react'
import { Plus, Info, Trash2, AlertCircle, Loader2 } from 'lucide-react'
import axios from 'axios'
import { RateInput, CurrencyInput } from './RateInput'
import NGNIcon from '../../assets/nigeria.svg'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import {
    useUrgentLiquiditySettings,
    useUpdateUrgentLiquiditySettings,
    useBulkCreateRepaymentRanges,
    useUpdateRepaymentRange,
    useDeleteRepaymentRange,
} from '@/hooks/useUrgentLiquidity'
import type { RepaymentRange } from '@/types/urgent-liquidity'
import { validateRanges, rangeErrorMap } from '@/lib/validateRanges'

const getErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
        const data = error.response?.data as {
            message?: string
            errors?: string[]
        }
        if (data?.errors?.length) return data.errors.join(', ')
        return data?.message || error.message
    }
    return error instanceof Error
        ? error.message
        : 'An unexpected error occurred'
}

interface LocalRepaymentRange {
    // undefined id = new (unsaved) range
    id?: string
    from: string
    to: string
    duration: string
}

interface SettingsOverrides {
    maxLoanRequest?: string
    loanFee?: string
    allowedLoanSettlement?: string
    collateral?: string
    liquidationDeadline?: string
    liquidationFee?: string
}

export const UrgentLiquidityTab = () => {
    const { data: settings, isLoading, error } = useUrgentLiquiditySettings()
    const updateSettingsMutation = useUpdateUrgentLiquiditySettings()
    const bulkCreateRangesMutation = useBulkCreateRepaymentRanges()
    const updateRangeMutation = useUpdateRepaymentRange()
    const deleteRangeMutation = useDeleteRepaymentRange()

    const [settingsOverrides, setSettingsOverrides] =
        useState<SettingsOverrides>({})
    const [repaymentRanges, setRepaymentRanges] = useState<
        LocalRepaymentRange[] | null
    >(null)
    const [settingsChanged, setSettingsChanged] = useState(false)

    // Derive effective settings: server data as base, user edits on top
    const maxLoanRequest =
        settingsOverrides.maxLoanRequest ??
        String(settings?.maxLoanRequest ?? '')
    const loanFee =
        settingsOverrides.loanFee ?? String(settings?.loanFeePercent ?? '')
    const allowedLoanSettlement =
        settingsOverrides.allowedLoanSettlement ??
        String(settings?.settlementPercent ?? '')
    const collateral =
        settingsOverrides.collateral ??
        String(settings?.collateralPercent ?? '')
    const liquidationDeadline =
        settingsOverrides.liquidationDeadline ??
        String(settings?.liquidationDeadlineDays ?? '')
    const liquidationFee =
        settingsOverrides.liquidationFee ??
        String(settings?.liquidationFeePercent ?? '')

    // Derive effective ranges: use edited ranges if any, otherwise server data
    const effectiveRanges = useMemo<LocalRepaymentRange[]>(() => {
        if (repaymentRanges !== null) return repaymentRanges
        if (settings?.repaymentRanges && settings.repaymentRanges.length > 0) {
            return settings.repaymentRanges.map((r: RepaymentRange) => ({
                id: r.id,
                from: String(r.fromAmount),
                to: String(r.toAmount),
                duration: String(r.repaymentDurationDays),
            }))
        }
        return [{ from: '', to: '', duration: '' }]
    }, [repaymentRanges, settings])

    // Validate ranges on every change
    const rangeErrors = useMemo(
        () => rangeErrorMap(validateRanges(effectiveRanges)),
        [effectiveRanges]
    )
    const hasRangeErrors = Object.keys(rangeErrors).length > 0

    const markChanged = () => setSettingsChanged(true)

    const addRepaymentRange = () => {
        setRepaymentRanges([
            ...effectiveRanges,
            { from: '', to: '', duration: '' },
        ])
        markChanged()
    }

    const removeRepaymentRange = (index: number) => {
        const range = effectiveRanges[index]
        if (range.id) {
            deleteRangeMutation.mutate(range.id, {
                onSuccess: () => {
                    setRepaymentRanges(prev =>
                        (prev ?? effectiveRanges).filter((_, i) => i !== index)
                    )
                    toast.success('Repayment range deleted')
                },
                onError: err => {
                    toast.error('Failed to delete repayment range', {
                        description: getErrorMessage(err),
                    })
                },
            })
        } else {
            setRepaymentRanges(effectiveRanges.filter((_, i) => i !== index))
            markChanged()
        }
    }

    const handleRangeChange = (
        index: number,
        field: keyof Omit<LocalRepaymentRange, 'id'>,
        value: string
    ) => {
        setRepaymentRanges(
            effectiveRanges.map((range, i) =>
                i === index ? { ...range, [field]: value } : range
            )
        )
        markChanged()
    }

    const handleSaveAll = async () => {
        try {
            // Save main settings
            await updateSettingsMutation.mutateAsync({
                maxLoanRequest: parseFloat(maxLoanRequest) || 0,
                loanFeePercent: parseFloat(loanFee) || 0,
                settlementPercent: parseFloat(allowedLoanSettlement) || 0,
                collateralPercent: parseFloat(collateral) || 0,
                liquidationDeadlineDays: Math.round(
                    parseFloat(liquidationDeadline) || 0
                ),
                liquidationFeePercent: parseFloat(liquidationFee) || 0,
            })

            // Save repayment ranges
            const newRanges = effectiveRanges.filter(r => !r.id)
            const existingRanges = effectiveRanges.filter(r => r.id)
            const rangePromises: Promise<unknown>[] = []

            if (newRanges.length > 0) {
                rangePromises.push(
                    bulkCreateRangesMutation.mutateAsync({
                        ranges: newRanges.map(r => ({
                            fromAmount: parseFloat(r.from) || 0,
                            toAmount: parseFloat(r.to) || 0,
                            repaymentDurationDays: Math.round(
                                parseFloat(r.duration) || 0
                            ),
                            currency: 'NGN',
                        })),
                    })
                )
            }

            existingRanges.forEach(r => {
                if (r.id) {
                    rangePromises.push(
                        updateRangeMutation.mutateAsync({
                            rangeId: r.id,
                            data: {
                                fromAmount: parseFloat(r.from) || 0,
                                toAmount: parseFloat(r.to) || 0,
                                repaymentDurationDays: Math.round(
                                    parseFloat(r.duration) || 0
                                ),
                                currency: 'NGN',
                            },
                        })
                    )
                }
            })

            await Promise.all(rangePromises)
            toast.success('Urgent liquidity settings saved')
            setSettingsChanged(false)
        } catch (err) {
            toast.error('Failed to save settings', {
                description: getErrorMessage(err),
            })
        }
    }

    const isSaving =
        updateSettingsMutation.isPending ||
        bulkCreateRangesMutation.isPending ||
        updateRangeMutation.isPending ||
        deleteRangeMutation.isPending

    if (isLoading) {
        return (
            <div className="space-y-8">
                <Skeleton className="h-8 w-full rounded-full" />
                <div className="grid gap-6 sm:grid-cols-2 md:w-[70%]">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-11 w-full" />
                    ))}
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex h-48 items-center justify-center">
                <p className="text-sm text-destructive">
                    Failed to load urgent liquidity settings.
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-8">
            {/* Main Header */}
            <div className="flex h-8 items-center rounded-full bg-[#5FA8B4] px-6">
                <h2 className="text-base font-medium text-white">
                    Urgent Liquidity
                </h2>
            </div>

            {/* Loan Settings */}
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#EDFBFD] px-6">
                    <h3 className="text-sm font-medium text-foreground">
                        Loan settings
                    </h3>
                </div>

                <div className="grid gap-6 sm:grid-cols-2 md:w-[70%]">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Maximum allowed loan request
                        </label>
                        <CurrencyInput
                            placeholder="Fill in maximum allowed request"
                            value={maxLoanRequest}
                            onChange={e => {
                                setSettingsOverrides(p => ({
                                    ...p,
                                    maxLoanRequest: e.target.value,
                                }))
                                markChanged()
                            }}
                            showPercentage
                        />
                        <div className="flex items-start gap-2 text-xs text-muted-foreground">
                            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                                This amount is with respect to selected
                                collateral
                            </span>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Fee (%)
                        </label>
                        <CurrencyInput
                            placeholder="Fill in fee"
                            value={loanFee}
                            onChange={e => {
                                setSettingsOverrides(p => ({
                                    ...p,
                                    loanFee: e.target.value,
                                }))
                                markChanged()
                            }}
                            showPercentage
                        />
                    </div>
                </div>
            </div>

            {/* Repayment Settings */}
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#EDFBFD] px-6">
                    <h3 className="text-sm font-medium text-foreground">
                        Repayment settings
                    </h3>
                </div>

                <div className="space-y-4 md:w-[70%]">
                    {effectiveRanges.map((range, index) => (
                        <div key={index} className="space-y-2">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">
                                        Amount range
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <RateInput
                                            placeholder="From"
                                            value={range.from}
                                            onChange={e =>
                                                handleRangeChange(
                                                    index,
                                                    'from',
                                                    e.target.value
                                                )
                                            }
                                            className={
                                                rangeErrors[index]
                                                    ? 'border-destructive focus-visible:ring-destructive'
                                                    : ''
                                            }
                                        />
                                        <RateInput
                                            placeholder="To"
                                            value={range.to}
                                            onChange={e =>
                                                handleRangeChange(
                                                    index,
                                                    'to',
                                                    e.target.value
                                                )
                                            }
                                            className={
                                                rangeErrors[index]
                                                    ? 'border-destructive focus-visible:ring-destructive'
                                                    : ''
                                            }
                                        />
                                        <div className="flex h-11 items-center gap-2 rounded-[12px] bg-secondary px-4">
                                            <img alt="Nigeria" src={NGNIcon} />
                                            <span className="text-sm font-medium text-foreground">
                                                NGN
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-foreground">
                                        Repayment duration
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <RateInput
                                            placeholder="0"
                                            value={range.duration}
                                            onChange={e =>
                                                handleRangeChange(
                                                    index,
                                                    'duration',
                                                    e.target.value
                                                )
                                            }
                                        />
                                        <div className="flex h-11 items-center gap-2 rounded-[12px] bg-secondary px-4">
                                            <span className="text-sm font-medium text-foreground">
                                                Days
                                            </span>
                                        </div>
                                        {effectiveRanges.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() =>
                                                    removeRepaymentRange(index)
                                                }
                                                disabled={
                                                    deleteRangeMutation.isPending &&
                                                    deleteRangeMutation.variables ===
                                                        range.id
                                                }
                                                className="h-11 w-11 p-0 text-destructive hover:text-destructive"
                                            >
                                                {deleteRangeMutation.isPending &&
                                                deleteRangeMutation.variables ===
                                                    range.id ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Trash2 className="h-4 w-4" />
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {rangeErrors[index] && (
                                <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                    <span>{rangeErrors[index]}</span>
                                </div>
                            )}
                        </div>
                    ))}

                    <Button
                        type="button"
                        variant="ghost"
                        onClick={addRepaymentRange}
                        className="h-auto gap-2 p-0 text-sm text-[#5FA8B4] hover:bg-transparent hover:text-[#5FA8B4]/80"
                    >
                        <Plus className="h-4 w-4" />
                        Add range
                    </Button>
                </div>
            </div>

            {/* Disbursement Settings */}
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#EDFBFD] px-6">
                    <h3 className="text-sm font-medium text-foreground">
                        Disbursement settings
                    </h3>
                </div>

                <div className="grid gap-6 sm:grid-cols-2 md:w-[70%]">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Allowed loan settlement (%)
                        </label>
                        <CurrencyInput
                            placeholder="Fill in allowed loan settlement"
                            value={allowedLoanSettlement}
                            onChange={e => {
                                setSettingsOverrides(p => ({
                                    ...p,
                                    allowedLoanSettlement: e.target.value,
                                }))
                                markChanged()
                            }}
                            showPercentage
                        />
                        <div className="flex items-start gap-2 text-xs text-muted-foreground">
                            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                                This amount that will be sent to the user
                            </span>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Collateral (%)
                        </label>
                        <CurrencyInput
                            placeholder="0"
                            value={collateral}
                            onChange={e => {
                                setSettingsOverrides(p => ({
                                    ...p,
                                    collateral: e.target.value,
                                }))
                                markChanged()
                            }}
                            showPercentage
                        />
                    </div>
                </div>
            </div>

            {/* Liquidation Settings */}
            <div className="space-y-4">
                <div className="flex h-8 items-center rounded-full bg-[#EDFBFD] px-6">
                    <h3 className="text-sm font-medium text-foreground">
                        Liquidation settings
                    </h3>
                </div>

                <div className="grid gap-6 sm:grid-cols-2 md:w-[70%]">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Liquidation deadline
                        </label>
                        <div className="flex items-center gap-2">
                            <RateInput
                                placeholder="0"
                                value={liquidationDeadline}
                                onChange={e => {
                                    setSettingsOverrides(p => ({
                                        ...p,
                                        liquidationDeadline: e.target.value,
                                    }))
                                    markChanged()
                                }}
                            />
                            <div className="flex h-11 items-center gap-2 rounded-[12px] bg-secondary px-4">
                                <span className="text-sm font-medium text-foreground">
                                    Days
                                </span>
                            </div>
                        </div>
                        <div className="flex items-start gap-2 text-xs text-muted-foreground">
                            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>
                                This is the amount of days the user has before
                                liquidation of collateral after loan due date
                            </span>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            Fee (%)
                        </label>
                        <CurrencyInput
                            placeholder="Fill in fee"
                            value={liquidationFee}
                            onChange={e => {
                                setSettingsOverrides(p => ({
                                    ...p,
                                    liquidationFee: e.target.value,
                                }))
                                markChanged()
                            }}
                            showPercentage
                        />
                    </div>
                </div>
            </div>

            {settingsChanged && !hasRangeErrors && (
                <div className="flex justify-start">
                    <Button
                        onClick={handleSaveAll}
                        disabled={isSaving}
                        className="rounded-full bg-[#17A2B8] px-8 text-white hover:bg-[#1493a7]"
                    >
                        {isSaving ? 'Saving...' : 'Save changes'}
                    </Button>
                </div>
            )}
        </div>
    )
}
